import { Flame, Target, Wind, Timer } from "lucide-react";
import { useCalculatorStore } from "@/store/calculatorStore";
import { CONVERT } from "@/utils/ballisticsMath";
import { fmt } from "@/utils/units";
import { useT } from "@/i18n/translations";

const MetricCard = ({ icon: Icon, label, value, unit, sub, testId }) => (
  <div
    className="relative overflow-hidden rounded-md border border-dase-border bg-dase-panel px-4 py-3"
    data-testid={testId}
  >
    <div className="absolute left-0 top-0 h-full w-0.5 bg-dase-red/70" />
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-dase-red" strokeWidth={2.2} />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">{label}</span>
    </div>
    <div className="mt-1.5 flex items-baseline">
      <span className="font-mono text-xl font-black tracking-tight text-zinc-100 sm:text-2xl">{value}</span>
      <span className="ml-1 text-xs font-bold text-dase-red">{unit}</span>
    </div>
    {sub && <div className="mt-0.5 text-[10px] font-medium text-zinc-500">{sub}</div>}
  </div>
);

export const MetricsPanel = ({ metrics }) => {
  const t = useT();
  const unitSystem = useCalculatorStore((s) => s.unitSystem);
  const imperial = unitSystem === "imperial";

  const e0 = imperial ? CONVERT.jToFtlb(metrics.e0J) : metrics.e0J;
  const eTgt = imperial ? CONVERT.jToFtlb(metrics.targetEnergyJ) : metrics.targetEnergyJ;
  const energyUnit = imperial ? "ft-lbs" : "J";

  const driftLinear = imperial ? CONVERT.mToIn(metrics.windDriftM) : metrics.windDriftM * 100;
  const driftLinUnit = imperial ? "in" : "cm";
  const driftDir = metrics.windDriftM >= 0 ? t("dir.right") : t("dir.left");

  const tgtDistDisp = imperial
    ? `${CONVERT.mToYd(metrics.targetDistanceM).toFixed(0)} yd`
    : `${metrics.targetDistanceM.toFixed(0)} m`;

  return (
    <div
      className="sticky top-16 z-30 border-b border-dase-border bg-dase-bg/95 backdrop-blur-md"
      data-testid="metrics-panel"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-3 px-4 py-3 md:grid-cols-4 md:gap-4 md:px-6 lg:px-8">
        <MetricCard
          icon={Flame}
          label={t("metrics.e0")}
          value={fmt(e0, 1)}
          unit={energyUnit}
          sub={t("metrics.e0sub")}
          testId="metric-e0-value"
        />
        <MetricCard
          icon={Target}
          label={t("metrics.residual")}
          value={fmt(eTgt, 1)}
          unit={energyUnit}
          sub={`@ ${tgtDistDisp}`}
          testId="metric-residual-energy-value"
        />
        <MetricCard
          icon={Wind}
          label={t("metrics.windDrift")}
          value={`${Math.abs(driftLinear).toFixed(1)}`}
          unit={driftLinUnit}
          sub={`${Math.abs(metrics.windDriftMrad).toFixed(2)} MRAD · ${driftDir}`}
          testId="metric-wind-drift-value"
        />
        <MetricCard
          icon={Timer}
          label={t("metrics.flightTime")}
          value={fmt(metrics.flightTimeS * 1000, 0)}
          unit="ms"
          sub={`@ ${tgtDistDisp}`}
          testId="metric-flight-time-value"
        />
      </div>
    </div>
  );
};

export default MetricsPanel;
